import {FC, Fragment, useEffect, useMemo, useState} from 'react'
import {Button, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import CopyToClipboard from 'react-copy-to-clipboard'
import {getPlatformSettingShortcodesAsync} from 'src/redux/actions/platformSettingActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import {parsePlatformSettingShortcodes} from 'src/utils/platformSettingShortcodeUtils'

type Props = {
  platformId?: number | null
  settingName?: string | null
  shortcodeOptions?: string[] | null
}

const MappingShortcodeChips: FC<Props> = ({platformId, settingName, shortcodeOptions}) => {
  const dispatch = useAppDispatch()
  const [shortcodes, setShortcodes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [copiedShortcode, setCopiedShortcode] = useState('')

  const presetShortcodes = useMemo(
    () =>
      Array.isArray(shortcodeOptions)
        ? shortcodeOptions.filter((item) => typeof item === 'string' && item.trim().length > 0)
        : [],
    [shortcodeOptions]
  )

  useEffect(() => {
    if (presetShortcodes.length) {
      setShortcodes(presetShortcodes)
      setLoadFailed(false)
      setLoading(false)
      return
    }

    if (!settingName) {
      setShortcodes([])
      setLoadFailed(true)
      setLoading(false)
      return
    }

    let isMounted = true
    setLoading(true)
    setLoadFailed(false)

    dispatch(
      getPlatformSettingShortcodesAsync({
        platform_id: platformId ?? undefined,
        setting_name: settingName,
      })
    )
      .then((response) => {
        if (!isMounted) return

        if (getPlatformSettingShortcodesAsync.rejected.match(response)) {
          setShortcodes([])
          setLoadFailed(true)
          return
        }

        const parsedShortcodes = parsePlatformSettingShortcodes(response.payload)
        setShortcodes(parsedShortcodes)
        setLoadFailed(parsedShortcodes.length === 0)
      })
      .catch(() => {
        if (isMounted) {
          setShortcodes([])
          setLoadFailed(true)
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [dispatch, platformId, presetShortcodes, settingName])

  const handleCopy = (shortcode: string) => {
    setCopiedShortcode(shortcode)
    window.setTimeout(() => setCopiedShortcode(''), 1000)
  }

  if (loading) {
    return (
      <div className='d-flex align-items-center mt-2'>
        <Spinner animation='border' size='sm' className='me-2' />
        <span className='fs-7 text-muted'>Loading shortcodes...</span>
      </div>
    )
  }

  if (loadFailed || !shortcodes.length) {
    return null
  }

  return (
    <div className='d-flex flex-wrap align-items-center mt-2'>
      <span className='fs-7 fw-medium text-secondary me-2'>Available Shortcodes:</span>
      {shortcodes.map((shortcode) => (
        <Fragment key={shortcode}>
          <OverlayTrigger
            placement='bottom'
            overlay={<Tooltip id={`${shortcode}-tooltip`}>{shortcode}</Tooltip>}
          >
            <CopyToClipboard text={shortcode}>
              <Button
                type='button'
                size='sm'
                variant='outline-secondary'
                className={`px-2 py-1 m-1 border border-secondary rounded fs-sm-8 ${
                  copiedShortcode === shortcode
                    ? 'text-white bg-secondary'
                    : 'text-secondary bg-white'
                }`}
                onClick={() => handleCopy(shortcode)}
              >
                {copiedShortcode === shortcode ? 'Copied!' : shortcode}
              </Button>
            </CopyToClipboard>
          </OverlayTrigger>
        </Fragment>
      ))}
    </div>
  )
}

export default MappingShortcodeChips
